import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGridPro, GridToolbarContainer, GridToolbarQuickFilter, itIT } from "@mui/x-data-grid-pro";
import { toast } from "react-toastify";
import { FaTrashAlt, FaUserCog, FaUserPlus } from "react-icons/fa";
import { IoCheckmarkOutline } from "react-icons/io5";
import { Edit } from "@mui/icons-material";
import api from "./api/api";
import { Modal } from "./utility/Modal";

const Toolbar = () => (
	<GridToolbarContainer>
		<div className="w-full flex items-center justify-end px-2 md:px-4 py-2">
			<GridToolbarQuickFilter
				placeholder="Cerca..."
				sx={{
					"& .MuiInputBase-input": {
						color: "var(--brand-dark)",
					},
					"& .MuiInputBase-root": {
						color: "var(--brand-dark)",
					},
					"& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--brand-border)" },
				}}
			/>
		</div>
	</GridToolbarContainer>
);

const emptyUser = {
	username: "",
	password: "",
	nome: "",
	cognome: "",
	email: "",
	rank: 0,
	token: 0,
	token_expiration: "",
	max_file: "",
};

const GestioneUtenze = () => {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedUsernames, setSelectedUsernames] = useState([]);
	const [openForm, setOpenForm] = useState(false);
	const [openDelete, setOpenDelete] = useState(false);
	const [formData, setFormData] = useState({ ...emptyUser });
	const [editingUser, setEditingUser] = useState(null);
	const rank = Number(sessionStorage.getItem("rank"));
	const navigate = useNavigate();

	const sortedUsers = useMemo(() => {
		return [...users].sort((a, b) => {
			if (a.username === "wpiccione") return -1;
			if (b.username === "wpiccione") return 1;
			if (a.rank !== b.rank) return Number(b.rank) - Number(a.rank);
			return a.username.localeCompare(b.username);
		});
	}, [users]);

	useEffect(() => {
		if (rank === 1) {
			loadUsers();
		} else {
			setLoading(false);
		}
	}, [rank]);

	const loadUsers = async () => {
		setLoading(true);
		try {
			const res = await api.get("admin/users");
			setUsers(res.data || []);
		} catch (error) {
			console.error(error);
			toast.error("Impossibile caricare le utenze.");
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setFormData({ ...emptyUser });
		setEditingUser(null);
	};

	const handleOpenCreate = () => {
		resetForm();
		setOpenForm(true);
	};

	const handleEdit = (row) => {
		setEditingUser(row.username);
		setFormData({
			username: row.username || "",
			password: "",
			nome: row.nome || "",
			cognome: row.cognome || "",
			email: row.email || "",
			rank: Number(row.rank) || 0,
			token: row.token ?? 0,
			token_expiration: row.token_expiration ? new Date(row.token_expiration).toISOString().slice(0, 10) : "",
			max_file: row.max_file ?? "",
		});
		setOpenForm(true);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const payload = {
			username: formData.username.trim(),
			password: formData.password ? formData.password.trim() : "",
			nome: formData.nome.trim(),
			cognome: formData.cognome.trim(),
			email: formData.email.trim(),
			rank: Number(formData.rank) || 0,
			token: Number(formData.token) || 0,
			token_expiration: formData.token_expiration ? formData.token_expiration : null,
			max_file: formData.max_file === "" ? null : Number(formData.max_file),
		};

		if (!payload.username || !payload.nome || !payload.cognome || !payload.email) {
			return toast.error("Compila tutti i campi obbligatori.");
		}

		if (!editingUser && !payload.password) {
			return toast.error("La password è obbligatoria per i nuovi utenti.");
		}

		try {
			if (editingUser) {
				if (!payload.password) {
					delete payload.password;
				}
				delete payload.username;
				await api.put(`admin/users/${editingUser}`, payload);
				toast.success("Utente aggiornato.");
			} else {
				await api.post("admin/users", payload);
				toast.success("Utente creato.");
			}
			setOpenForm(false);
			resetForm();
			loadUsers();
		} catch (error) {
			const message = error?.response?.data?.message;
			if (message) {
				toast.error(message);
			} else {
				toast.error("Errore durante il salvataggio dell'utente.");
			}
		}
	};

	const handleDelete = async () => {
		try {
			await api.delete("admin/users", { data: { usernames: selectedUsernames } });
			toast.success("Utenti eliminati.");
			setOpenDelete(false);
			setSelectedUsernames([]);
			loadUsers();
		} catch (error) {
			const message = error?.response?.data?.message;
			if (message) {
				toast.error(message);
			} else {
				toast.error("Errore durante l'eliminazione.");
			}
		}
	};

	const handleGhostLogin = async (row) => {
		if (Number(row.rank) >= rank) {
			return toast.error("Ghost login consentito solo su livelli inferiori.");
		}
		try {
			const currentUsername = sessionStorage.getItem("username");
			if (!sessionStorage.getItem("ghost")) {
				sessionStorage.setItem("ghostAdminUsername", currentUsername || "");
				sessionStorage.setItem("ghostAdminEmail", sessionStorage.getItem("email") || "");
				sessionStorage.setItem("ghostAdminNome", sessionStorage.getItem("nome") || "");
				sessionStorage.setItem("ghostAdminRank", sessionStorage.getItem("rank") || "1");
			}

			const res = await api.post("admin/users/ghost-login", { username: row.username });
			sessionStorage.setItem("logged", "ok");
			sessionStorage.setItem("username", res.data.username);
			sessionStorage.setItem("email", res.data.email);
			sessionStorage.setItem("nome", `${res.data.nome} ${res.data.cognome}`.trim());
			sessionStorage.setItem("rank", res.data.rank);
			sessionStorage.setItem("ghost", "true");
			window.dispatchEvent(new Event("ghost-changed"));
			toast.success(`Sei in ghost login come ${res.data.username}.`);
			navigate("/Homepage");
		} catch (error) {
			const message = error?.response?.data?.message;
			if (message) {
				toast.error(message);
			} else {
				toast.error("Errore durante il ghost login.");
			}
		}
	};

	const columns = [
		{ field: "username", headerName: "Username", flex: 1, align: "center", headerAlign: "center" },
		{ field: "nome", headerName: "Nome", flex: 1, align: "center", headerAlign: "center" },
		{ field: "cognome", headerName: "Cognome", flex: 1, align: "center", headerAlign: "center" },
		{ field: "email", headerName: "Email", flex: 1.4, align: "center", headerAlign: "center" },
		{
			field: "rank",
			headerName: "Ruolo",
			width: 140,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<span
					className={`px-3 py-1 rounded text-xs font-semibold ${
						Number(params.value) === 1 ? "bg-[rgba(35,100,174,0.15)] text-[var(--brand-dark)]" : "bg-[rgba(39,55,62,0.1)] text-[var(--brand-dark)]"
					}`}
				>
					{Number(params.value) === 1 ? "Admin" : "Utente"}
				</span>
			),
		},
		{ field: "token", headerName: "Token", width: 120, align: "center", headerAlign: "center" },
		{
			field: "token_expiration",
			headerName: "Scadenza Token",
			width: 180,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (params.value ? new Date(params.value).toLocaleDateString("it-IT") : "—"),
		},
		{ field: "max_file", headerName: "Max File", width: 130, align: "center", headerAlign: "center", valueGetter: (params) => params.row.max_file ?? "—" },
		{
			field: "actions",
			headerName: "Azioni",
			width: 200,
			sortable: false,
			filterable: false,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<div className="flex items-center gap-2 justify-center">
					<button
						type="button"
						className="button-prophet px-3 py-2 text-sm"
						style={{
							borderRadius: "999px",
							background: "var(--brand-dark)",
							color: "white",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							paddingInline: "12px",
							paddingBlock: "8px",
						}}
						onClick={() => handleEdit(params.row)}
						aria-label="Modifica utente"
					>
						<Edit fontSize="small" sx={{ color: "white" }} />
					</button>
					{Number(params.row.rank) < rank && (
						<button
							type="button"
							className="button-prophet px-3 py-2 text-sm"
							style={{
								borderRadius: "999px",
								background: "linear-gradient(135deg, rgba(35,100,174,0.85), rgba(39,55,62,0.85))",
								color: "white",
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								paddingInline: "12px",
								paddingBlock: "8px",
							}}
							onClick={() => handleGhostLogin(params.row)}
							aria-label="Ghost login"
						>
							<FaUserCog className="text-white" />
						</button>
					)}
				</div>
			),
		},
	];

	if (rank !== 1) {
		return (
			<div className="w-full h-full flex items-center justify-center">
				<div className="bg-white/20 shadow-lg ring-1 ring-black/5 backdrop-blur-md p-6 text-[var(--brand-dark)] text-center max-w-xl">
					<h2 className="text-2xl font-semibold mb-2">Accesso limitato</h2>
					<p className="text-base font-light">Solo gli amministratori possono gestire le utenze.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="relative flex flex-col w-full gap-4 text-[var(--brand-dark)]">
			<div className="flex flex-wrap gap-2 justify-end items-center">
				<div className="flex gap-2">
					{selectedUsernames.length > 0 && (
						<button
							type="button"
							className="button-prophet"
							style={{
								borderRadius: "999px",
								background: "linear-gradient(135deg, rgba(192,57,43,0.75), rgba(192,57,43,0.9))",
								color: "white",
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								paddingInline: "12px",
								paddingBlock: "8px",
							}}
							onClick={() => setOpenDelete(true)}
						>
							<FaTrashAlt className="text-lg" />
						</button>
					)}
				</div>
			</div>
			<div
				className="w-full bg-white/10 shadow-lg ring-1 ring-black/5 backdrop-blur-md rounded-md"
				style={{ height: "calc(100vh - 230px)", minHeight: "60vh" }}
			>
				<DataGridPro
					localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
					rows={sortedUsers}
					loading={loading}
					columns={columns}
					getRowId={(row) => row.username}
					checkboxSelection
					disableRowSelectionOnClick
					onRowSelectionModelChange={(ids) => setSelectedUsernames(ids)}
					hideFooterSelectedRowCount
					hideFooterRowCount
					slots={{ toolbar: Toolbar }}
					slotProps={{
						pagination: {
							labelDisplayedRows: () => "",
						},
					}}
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					sx={{
						color: "var(--brand-dark)",
						"& .MuiDataGrid-cell": { color: "var(--brand-dark)" },
						"& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle": { color: "var(--brand-dark)" },
						"& .MuiDataGrid-columnHeaders": { color: "var(--brand-dark)" },
						"& .MuiDataGrid-row": { color: "var(--brand-dark)" },
						"& .MuiTablePagination-toolbar": {
							flexWrap: "wrap",
							justifyContent: "space-between",
						},
						"& .MuiTablePagination-displayedRows": {
							display: "none !important",
						},
						"& .MuiTablePagination-actions": {
							marginLeft: "auto",
						},
					}}
				/>
			</div>

			<button
				type="button"
				onClick={handleOpenCreate}
				className="button-prophet"
				style={{
					position: "absolute",
					right: "24px",
					bottom: "24px",
					borderRadius: "999px",
					background: "linear-gradient(135deg, rgba(35,100,174,0.85), rgba(39,55,62,0.85))",
					color: "white",
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					paddingInline: "14px",
					paddingBlock: "10px",
					boxShadow: "0 10px 30px rgba(39,55,62,0.25)",
				}}
				aria-label="Nuovo utente"
			>
				<span className="text-2xl text-white leading-none">+</span>
			</button>

			<Modal
				open={openForm}
				handleClose={() => {
					setOpenForm(false);
					resetForm();
				}}
			>
				<form className="flex flex-col gap-4 text-[var(--brand-dark)]" onSubmit={handleSubmit}>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{[
							{ name: "username", label: "Username*", type: "text", placeholder: "es. mrossi", disabled: Boolean(editingUser) },
							{ name: "password", label: editingUser ? "Password*" : "Password*", helper: editingUser ? "Lascia vuoto per non modificare" : "" , type: "password", placeholder: "••••••••" },
							{ name: "nome", label: "Nome*", type: "text", placeholder: "Nome" },
							{ name: "cognome", label: "Cognome*", type: "text", placeholder: "Cognome" },
							{ name: "email", label: "Email*", type: "email", placeholder: "nome.cognome@mail.com" },
						].map((field) => (
							<div key={field.name} className="flex flex-col gap-1">
								<label className="label-text">{field.label}</label>
								<input
									type={field.type}
									name={field.name}
									value={formData[field.name]}
									onChange={handleChange}
									className="input-text"
									disabled={field.disabled}
									placeholder={field.placeholder}
								/>
								{field.helper ? <span className="text-xs text-[var(--brand-dark)]/80">{field.helper}</span> : null}
							</div>
						))}
						<div className="flex flex-col gap-1">
							<label className="label-text">Ruolo</label>
							<select name="rank" value={formData.rank} onChange={handleChange} className="input-text bg-white text-[var(--brand-dark)]">
								<option value={0}>Utente</option>
								<option value={1}>Admin</option>
							</select>
						</div>
						<div className="flex flex-col gap-1">
							<label className="label-text">Token disponibili</label>
							<input type="number" name="token" value={formData.token} onChange={handleChange} className="input-text w-full input-number-clean" min={0} />
						</div>
						<div className="flex flex-col gap-1">
							<label className="label-text">Scadenza token</label>
							<input type="date" name="token_expiration" value={formData.token_expiration || ""} onChange={handleChange} className="input-text" />
						</div>
						<div className="flex flex-col gap-1">
							<label className="label-text">Limite file caricabili</label>
							<input type="number" name="max_file" value={formData.max_file ?? ""} onChange={handleChange} className="input-number w-full" min={0} />
						</div>
					</div>
				<div className="flex justify-end gap-2 pt-2">
					<button
						type="button"
						onClick={() => {
							setOpenForm(false);
							resetForm();
						}}
						className="button-prophet"
						style={{ borderRadius: "999px", background: "linear-gradient(135deg, rgba(35,100,174,0.25), rgba(39,55,62,0.45))" }}
					>
						<span className="text-lg text-white leading-none">←</span>
					</button>
					<button
						type="submit"
						className="button-prophet"
						style={{ borderRadius: "999px", background: "linear-gradient(135deg, rgba(35,100,174,0.55), rgba(39,55,62,0.65))", color: "white" }}
					>
						<span className="text-xl text-white leading-none">✔</span>
					</button>
				</div>
			</form>
		</Modal>

			<Modal
				open={openDelete}
				handleClose={() => {
					setOpenDelete(false);
				}}
			>
				<div className="flex flex-col text-[var(--brand-dark)] gap-4">
					<h3 className="text-2xl font-semibold text-center">Confermi l'eliminazione?</h3>
					<div className="flex flex-col gap-1 max-h-[40vh] overflow-auto">
						{selectedUsernames.map((u) => (
							<span key={u} className="text-center">
								{u}
							</span>
						))}
					</div>
					<div className="flex justify-end gap-2">
						<button
							type="button"
							className="button-prophet"
							style={{ borderRadius: "999px", background: "linear-gradient(135deg, rgba(35,100,174,0.25), rgba(39,55,62,0.45))" }}
							onClick={() => setOpenDelete(false)}
						>
							<span className="text-lg text-white leading-none">←</span>
						</button>
						<button
							type="button"
							className="button-prophet"
							style={{ borderRadius: "999px", background: "linear-gradient(135deg, rgba(192,57,43,0.75), rgba(192,57,43,0.9))" }}
							onClick={handleDelete}
						>
							<FaTrashAlt className="text-lg text-white" />
						</button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default GestioneUtenze;
